import { useState, useEffect, useRef } from "react";
import { useToast } from "@/hooks/use-toast";
import { toast as sonnerToast } from "sonner";
import { logger } from "@/lib/logger";
import { persistentLogger } from "@/lib/persistent-logger";
import { supabase } from "@/integrations/supabase/client";
import { Capacitor } from '@capacitor/core';
import { spotifyTokenCache } from "@/lib/spotify-token-cache";
import { saveOAuthData } from "@/lib/oauth-storage";

export const SPOTIFY_AUTH_TIMEOUT_MS = 15000;

interface SpotifyConfig {
  clientId: string;
  redirectUri: string;
  isNative?: boolean;
}

export const useSpotifyAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isConfigReady, setIsConfigReady] = useState(false);
  const [config, setConfig] = useState<SpotifyConfig | null>(null);
  const { toast } = useToast();
  const authTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    fetchConfig();
    checkAuthStatus();

    // ✅ Listen for successful OAuth callback (triggered by SpotifyDeepLinkListener)
    const handleAuthSuccess = () => {
      logger.info('✅ Spotify auth success event received');
      setIsLoading(false);
      if (authTimeoutRef.current) {
        clearTimeout(authTimeoutRef.current);
        authTimeoutRef.current = null;
      }
      // ✅ FIX: Remover classe de OAuth quando callback chegar
      document.body.classList.remove('spotify-oauth-active');
      checkAuthStatus(); // Revalidate auth status
    };

    window.addEventListener('spotifyAuthSuccess', handleAuthSuccess);

    return () => {
      window.removeEventListener('spotifyAuthSuccess', handleAuthSuccess);
      if (authTimeoutRef.current) {
        clearTimeout(authTimeoutRef.current);
      }
    };
  }, []);

  const fetchConfig = async () => {
    try {
      const platform = Capacitor.getPlatform();
      const isNative = Capacitor.isNativePlatform();
      
      logger.info('Fetching Spotify config', { 
        platform, 
        isNative,
        capacitorVersion: Capacitor.getPlatform() 
      });

      const { data, error } = await supabase.functions.invoke('get-spotify-config', {
        body: { platform }
      });
      
      if (error) throw error;
      
      // ✅ FASE 1: Validar se data tem os campos necessários
      if (!data || !data.clientId || !data.redirectUri) {
        logger.error('Spotify config incomplete or missing', { data });
        setIsConfigReady(false);
        toast({
          title: "Spotify não configurado",
          description: "Entre em contato com o suporte ou tente novamente mais tarde.",
          variant: "destructive",
        });
        return;  // ✅ Retornar SEM setar config
      }
      
      // ✅ FIX: Simplified redirect URI (easier to configure in Spotify Dashboard)
      let redirectUri = data.redirectUri;
      
      // ✅ CORREÇÃO: Validar redirect URI e forçar deep link nativo se inválido
      const invalidPrefixes = ['capacitor://', 'ionic://', 'file://'];
      if (invalidPrefixes.some(prefix => redirectUri.startsWith(prefix))) {
        logger.warn('Invalid redirect URI detected, forcing native deep link', { 
          receivedUri: redirectUri,
          platform,
          isNative 
        });
        redirectUri = 'youbetterrun://oauth/spotify/callback';
      }
      
      if (isNative) {
        // Use backend-provided native deep link (must match Spotify Dashboard)
        logger.info('🔗 Using native redirect URI from backend', { redirectUri, platform });
      } else {
        // For web, use the backend-provided callback bound to current origin
        logger.info('🌐 Using web redirect URI from backend', { redirectUri });
      }
      
      // Validar redirect URI retornado
      const isDeepLink = redirectUri.startsWith('youbetterrun://') || redirectUri.startsWith('com.youbetterrun.app://');
      
      logger.info('✅ Spotify config finalized', {
        redirectUri,
        isNative,
        isDeepLink,
        platformMatch: isNative === isDeepLink,
        clientIdLength: data.clientId?.length || 0
      });
      
      // ✅ Log final redirect URI for debugging
      console.log('🎵 [SPOTIFY] Final Redirect URI:', redirectUri);
      
      setConfig({
        clientId: data.clientId,
        redirectUri,
        isNative
      });
      setIsConfigReady(true);
    } catch (error) {
      logger.error('Failed to fetch Spotify config', error);
      setIsConfigReady(false);
      toast({
        title: "Spotify não configurado",
        description: "Falha ao carregar configuração do Spotify. Entre em contato com o suporte ou tente novamente mais tarde.",
        variant: "destructive",
      });
    }
  };

  const checkAuthStatus = async () => {
    const span = logger.startSpan('checkSpotifyAuth');
    
    if (authTimeoutRef.current) {
      clearTimeout(authTimeoutRef.current);
      authTimeoutRef.current = null;
    }
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        span.end('success', { authenticated: false });
        return;
      }

      logger.info('Checking Spotify connection', { userId: user.id });
      persistentLogger.addLog('SPOTIFY', 'info', 'Checking auth status', { userId: user.id });
      
      // Check cache and validate expiration
      const cachedToken = await spotifyTokenCache.getToken(user.id);
      if (cachedToken) {
        const expiresAt = new Date(cachedToken.expiresAt);
        const now = new Date();
        const timeToExpiry = expiresAt.getTime() - now.getTime();
        
        // Auto-refresh if expires in less than 5 minutes
        if (timeToExpiry < 5 * 60 * 1000) {
          logger.warn('Token expires soon, auto-refreshing', { expiresAt, timeToExpiry });
          persistentLogger.addLog('SPOTIFY', 'warn', 'Token expiring, auto-refresh', { timeToExpiry });
          
          const refreshed = await refreshSpotifyToken(user.id);
          if (refreshed) {
            setIsAuthenticated(true);
            span.end('success', { authenticated: true, autoRefreshed: true });
            return;
          }
        } else {
          setIsAuthenticated(true);
          span.end('success', { authenticated: true, source: 'cache' });
          return;
        }
      }

      // Check database
      const response = await supabase
        .from('music_connections')
        .select('access_token, expires_at, device_id, metadata')
        .eq('user_id', user.id)
        .eq('provider', 'spotify')
        .maybeSingle();

      if (response.error) {
        logger.error('Error querying music_connections', response.error);
        persistentLogger.addLog('SPOTIFY', 'error', 'DB query failed', { error: response.error });
        span.end('error', { error: response.error.message });
        return;
      }
      
      const connection = response.data;
      if (!connection) {
        await spotifyTokenCache.deleteToken(user.id);
        span.end('success', { authenticated: false });
        return;
      }

      // Check if expired
      const isExpired = new Date(connection.expires_at) < new Date();
      if (isExpired) {
        logger.warn('Token expired, attempting refresh');
        persistentLogger.addLog('SPOTIFY', 'warn', 'Token expired', { expiresAt: connection.expires_at });
        
        const refreshed = await refreshSpotifyToken(user.id);
        if (refreshed) {
          setIsAuthenticated(true);
          span.end('success', { authenticated: true, tokenRefreshed: true });
        } else {
          setIsAuthenticated(false);
          span.end('success', { authenticated: false, refreshFailed: true });
        }
        return;
      }

      // Save to cache
      await spotifyTokenCache.setToken({
        userId: user.id,
        accessToken: connection.access_token,
        expiresAt: connection.expires_at,
        deviceId: connection.device_id || undefined,
        metadata: connection.metadata as Record<string, any> | undefined
      });

      setIsAuthenticated(true);
      span.end('success', { authenticated: true, source: 'supabase' });
    } catch (error) {
      logger.error('Error checking Spotify auth', error);
      persistentLogger.addLog('SPOTIFY', 'error', 'Auth check failed', { error });
      span.end('error', { error });
    }
  };

  // Helper to refresh token with retry
  const refreshSpotifyToken = async (userId: string, retryCount = 0): Promise<boolean> => {
    try {
      const { data, error } = await supabase.functions.invoke('refresh-music-token', {
        body: { provider: 'spotify' }
      });
      
      if (error || !data?.access_token) {
        logger.error('Token refresh failed', error);
        persistentLogger.addLog('SPOTIFY', 'error', 'Refresh failed', { error, retryCount });
        
        // Retry once after 2 seconds
        if (retryCount === 0) {
          await new Promise(resolve => setTimeout(resolve, 2000));
          return refreshSpotifyToken(userId, retryCount + 1);
        }
        
        await spotifyTokenCache.deleteToken(userId);
        return false;
      }
      
      logger.info('Token refreshed successfully');
      persistentLogger.addLog('SPOTIFY', 'info', 'Token refreshed', { retryCount });
      
      // Fetch updated token
      const refreshedResponse = await supabase
        .from('music_connections')
        .select('access_token, expires_at, device_id, metadata')
        .eq('user_id', userId)
        .eq('provider', 'spotify')
        .maybeSingle();
      
      if (refreshedResponse.data) {
        await spotifyTokenCache.setToken({
          userId,
          accessToken: refreshedResponse.data.access_token,
          expiresAt: refreshedResponse.data.expires_at,
          deviceId: refreshedResponse.data.device_id || undefined,
          metadata: refreshedResponse.data.metadata as Record<string, any> | undefined
        });
        return true;
      }
      
      return false;
    } catch (error) {
      logger.error('Error during token refresh', error);
      persistentLogger.addLog('SPOTIFY', 'error', 'Refresh exception', { error, retryCount });
      
      if (retryCount === 0) {
        await new Promise(resolve => setTimeout(resolve, 2000));
        return refreshSpotifyToken(userId, retryCount + 1);
      }
      
      return false;
    }
  };

  // Função auxiliar para sincronização em background
  const syncCacheWithSupabase = async (userId: string) => {
    try {
      const response = await supabase
        .from('music_connections')
        .select('access_token, expires_at, device_id, metadata')
        .eq('user_id', userId)
        .eq('provider', 'spotify')
        .maybeSingle();

      if (response.data) {
        await spotifyTokenCache.setToken({
          userId,
          accessToken: response.data.access_token,
          expiresAt: response.data.expires_at,
          deviceId: response.data.device_id || undefined,
          metadata: response.data.metadata as Record<string, any> | undefined
        });
        logger.info('Cache synced with Supabase', { userId });
      }
    } catch (error) {
      logger.error('Failed to sync cache with Supabase', error);
    }
  };

  const login = async (forceMethod?: 'native' | 'web') => {
    const span = logger.startSpan('spotifyLogin');
    const correlationId = logger.getCorrelationId();
    logger.info('Spotify login initiated', { correlationId, forceMethod });
    persistentLogger.addLog('SPOTIFY', 'info', 'OAuth login started', { 
      method: forceMethod || 'auto',
      correlationId 
    });
    
    // ✅ FIX: Limpar overlays e modais ANTES de iniciar OAuth
    // Remove classes que podem causar z-index issues
    document.body.classList.remove('ybr-modal-open', 'ybr-dialog-open');
    
    // Fechar todos os toasts/sonner ativos
    const toastElements = document.querySelectorAll('[data-sonner-toast]');
    toastElements.forEach(el => el.remove());
    
    // Adicionar classe para forçar z-index correto durante OAuth
    document.body.classList.add('spotify-oauth-active');
    
    logger.info('Cleared overlays and modals before OAuth', { 
      removedModalClasses: ['ybr-modal-open', 'ybr-dialog-open'],
      removedToasts: toastElements.length 
    });
    
    setIsLoading(true);
    
    // ✅ FASE 2: Verificar config ANTES de mostrar toast de "Conectando..."
    if (!isConfigReady || !config?.clientId) {
      logger.warn('Config not ready or missing', { isConfigReady, hasConfig: !!config });
      setIsLoading(false);
      sonnerToast.error("Spotify não configurado", {
        description: "O Spotify não está configurado corretamente. Veja as instruções de configuração.",
        duration: 8000,
        action: {
          label: "Ver configuração",
          onClick: () => window.location.href = '/spotify-setup'
        }
      });
      span.end('error', { reason: 'no_config' });
      return;
    }
    
    // Toast inicial informativo
    toast({
      title: "🎵 Conectando com Spotify",
      description: "Você será redirecionado para autorizar o acesso...",
    });
    
    // ✅ FASE 4: Timeout para o processo de autenticação
    const timeout = setTimeout(() => {
      logger.warn(`Spotify auth timeout after ${SPOTIFY_AUTH_TIMEOUT_MS}ms`);
      setIsLoading(false);
      toast({
        title: "Tempo esgotado para autenticar com o Spotify",
        description: "Feche esta tela e tente conectar novamente.",
        variant: "destructive",
      });
      span.end('error', { reason: 'timeout' });
    }, SPOTIFY_AUTH_TIMEOUT_MS);
    
    authTimeoutRef.current = timeout;  // ✅ Armazenar ref
    
    try {

      // Verificar preferência salva do usuário
      const savedPreference = localStorage.getItem('spotify_auth_preference') as 'native' | 'web' | null;
      const useMethod = forceMethod || savedPreference || (config.isNative ? 'native' : 'web');
      
      logger.info('Spotify auth method selected', {
        useMethod,
        forceMethod,
        savedPreference,
        configIsNative: config.isNative
      });

      const codeVerifier = generateRandomString(128);
      const codeChallenge = await generateCodeChallenge(codeVerifier);
      const state = generateRandomString(16);
      
      // ✅ CORREÇÃO CRÍTICA: Usar Preferences API para persistência cross-process (Safari OAuth -> Capacitor WebView)
      // Isso garante que os dados sobrevivam ao iOS matar o app em background
      await saveOAuthData('spotify_code_verifier', codeVerifier);
      await saveOAuthData('spotify_state', state);
      
      logger.info('📱 OAuth PKCE data saved with native persistence', {
        timestamp: new Date().toISOString(),
        platform: Capacitor.getPlatform(),
        isNative: Capacitor.isNativePlatform()
      });
      
      persistentLogger.addLog('OAUTH', 'info', 'OAuth PKCE data saved', {
        hasCodeVerifier: !!codeVerifier,
        hasState: !!state,
        platform: Capacitor.getPlatform()
      });

      const platform = Capacitor.getPlatform();
      const isNativePlatform = Capacitor.isNativePlatform();
      
      // Determinar redirect URI baseado no método escolhido
      let redirectUri = config.redirectUri;
      if (useMethod === 'web' && isNativePlatform) {
        // Forçar web redirect mesmo em plataforma nativa
        redirectUri = `${window.location.origin}/oauth/spotify/callback`;
        logger.info('Forcing web redirect on native platform', { redirectUri });
      }

      const isDeepLink = redirectUri.startsWith('youbetterrun://');

      // Validação crítica: deep link só deve ser usado em plataforma nativa
      if (isDeepLink && !isNativePlatform) {
        logger.error('Critical error: deep link redirect on web platform', {
          platform,
          redirectUri
        });
        throw new Error('Invalid redirect URI configuration for platform');
      }

      const authUrl = new URL('https://accounts.spotify.com/authorize');
      authUrl.searchParams.set('client_id', config.clientId);
      authUrl.searchParams.set('response_type', 'code');
      authUrl.searchParams.set('redirect_uri', redirectUri);
      authUrl.searchParams.set('code_challenge_method', 'S256');
      authUrl.searchParams.set('code_challenge', codeChallenge);
      authUrl.searchParams.set('scope', 'streaming user-read-email user-read-private user-modify-playback-state user-read-playback-state');
      authUrl.searchParams.set('state', state);

      // Salvar dados com persistência nativa para sobreviver app restart
      await saveOAuthData('spotify_redirect_uri', redirectUri);
      await saveOAuthData('spotify_platform', platform);
      await saveOAuthData('spotify_auth_method', useMethod);
      await saveOAuthData('spotify_auth_attempt', Date.now().toString());

      logger.info('Opening Spotify OAuth', { 
        correlationId,
        platform,
        isNativePlatform,
        isDeepLink,
        useMethod,
        redirectUri,
        isNativeConfig: config.isNative,
        clientId: config.clientId.substring(0, 8) + '...',
        authUrl: authUrl.toString().substring(0, 100) + '...'
      });
      
      // Toast específico baseado no método
      if (useMethod === 'native' && isNativePlatform) {
        toast({
          title: "📱 Abrindo app do Spotify",
          description: "Se o app não estiver instalado, abrirá no navegador...",
        });
      } else {
        toast({
          title: "🌐 Abrindo no navegador",
          description: "Você será redirecionado para autorizar o acesso...",
        });
      }
      
      // iOS/Android Native: Use Browser plugin for OAuth
      if (isNativePlatform && useMethod === 'native') {
        try {
          const { Browser } = await import('@capacitor/browser');
          
          // ✅ Aguardar um frame antes de abrir para garantir que overlays foram removidos
          await new Promise(resolve => requestAnimationFrame(resolve));
          
          // ✅ FASE 3: NÃO usar await - apenas disparar Browser.open
          Browser.open({
            url: authUrl.toString(),
            windowName: '_self',
            toolbarColor: '#000000',
            presentationStyle: 'popover',
          }).then(() => {
            logger.info('Browser opened successfully for Spotify OAuth');
            // ✅ Timeout será limpo quando callback chegar (handleCallback)
            // Remover classe quando OAuth terminar
            document.body.classList.remove('spotify-oauth-active');
          }).catch((error) => {
            logger.warn('Native browser failed, trying web fallback', error);
            clearTimeout(timeout);
            
            toast({
              title: "⚠️ Tentando método alternativo",
              description: "Abrindo no navegador web...",
            });
            
            setIsLoading(false);
            
            // Retry com método web
            setTimeout(() => login('web'), 1000);
          });
          
          // Salvar preferência do usuário
          localStorage.setItem('spotify_auth_preference', 'native');
          
          span.end('success', { platform: 'native', method: useMethod });
        } catch (error) {
          logger.error('Error importing Browser module', error);
          clearTimeout(timeout);
          setIsLoading(false);
          toast({
            title: "❌ Erro ao conectar",
            description: "Não foi possível abrir o navegador.",
            variant: "destructive",
          });
          span.end('error', { reason: 'browser_import_failed' });
        }
      } else {
        // Web: Standard redirect
        // ✅ Aguardar um frame antes de redirecionar para garantir que overlays foram removidos
        await new Promise(resolve => requestAnimationFrame(resolve));
        window.location.href = authUrl.toString();
        clearTimeout(timeout);
        
        // Salvar preferência do usuário
        if (forceMethod) {
          localStorage.setItem('spotify_auth_preference', forceMethod);
        }
        
        span.end('success', { platform: 'web', method: useMethod });
      }
    } catch (error) {
      clearTimeout(timeout);
      logger.error('Spotify login error', error, { correlationId });
      setIsLoading(false);
      
      // ✅ FIX: Remover classe de OAuth em caso de erro
      document.body.classList.remove('spotify-oauth-active');
      
      // Mensagens de erro específicas com retry logic
      let title = "❌ Erro ao conectar";
      let description = "Não foi possível conectar ao Spotify.";
      let shouldRetry = false;
      
      if (error instanceof Error) {
        if (error.message.includes('network')) {
          title = "🌐 Erro de conexão";
          description = "Verifique sua internet e tente novamente.";
        } else if (error.message.includes('refused') || error.message.includes('redirect_uri')) {
          title = "🚫 Redirect URI incorreto";
          description = "Tentando método alternativo...";
          shouldRetry = true;
        } else {
          description = error.message;
        }
      }
      
      // Se deve tentar novamente com método alternativo
      if (shouldRetry && !forceMethod && config.isNative) {
        logger.warn('Native auth failed, retrying with web method', { error });
        
        toast({
          title: "⚠️ Tentando método alternativo",
          description: "Abrindo no navegador web...",
        });
        
        // Aguardar 1s antes de retry
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        return login('web');
      }
      
      // Erro final com opção de escolher método
      if (config.isNative && !forceMethod) {
        sonnerToast.error(title, {
          description: description + " Deseja tentar com o navegador?",
          duration: 10000,
          action: {
            label: "Usar navegador",
            onClick: () => {
              logger.info('User chose web method after error');
              login('web');
            }
          }
        });
      } else {
        toast({
          title,
          description,
          variant: "destructive",
        });
      }
      
      // ✅ Special handling for redirect URI errors
      if (error instanceof Error && (error.message.includes('redirect_uri') || error.message.includes('redirect'))) {
        sonnerToast.error("❌ Erro de Configuração", {
          description: "O Redirect URI não está configurado corretamente no Spotify Developer Dashboard.",
          duration: 10000,
          action: {
            label: "Ver instruções",
            onClick: () => {
              logger.info('User navigating to Spotify setup page');
              window.location.href = '/spotify-setup';
            }
          }
        });
      }
      
      span.end('error', { error, forceMethod });
    }
  };

  const logout = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // ✅ 1. Limpar cache local primeiro
      await spotifyTokenCache.deleteToken(user.id);
      logger.info('Cleared Spotify token from cache', { userId: user.id });

      // ✅ 2. Remover do Supabase
      await supabase
        .from('music_connections')
        .delete()
        .eq('user_id', user.id)
        .eq('provider', 'spotify');

      // Limpar preferências salvas
      localStorage.removeItem('spotify_auth_preference');

      setIsAuthenticated(false);
      toast({
        title: "✅ Desconectado do Spotify",
        description: "Você pode reconectar a qualquer momento.",
      });
    } catch (error) {
      logger.error('Spotify logout error', error);
      // ✅ Even on error, mark as not authenticated locally
      setIsAuthenticated(false);
      toast({
        title: "Erro ao desconectar",
        description: "Tente novamente.",
        variant: "destructive",
      });
    }
  };

  const resetAuthPreference = () => {
    localStorage.removeItem('spotify_auth_preference');
    logger.info('Spotify auth preference reset');
    toast({
      title: "✅ Preferência redefinida",
      description: "O método de autenticação será detectado automaticamente na próxima vez.",
    });
  };

  return {
    isAuthenticated,
    isLoading,
    isConfigReady,
    login,
    logout,
    resetAuthPreference,
  };
};

function generateRandomString(length: number): string {
  const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const values = crypto.getRandomValues(new Uint8Array(length));
  return values.reduce((acc, x) => acc + possible[x % possible.length], '');
}

async function generateCodeChallenge(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/=/g, '')
    .replace(/\+/g, '-')
    .replace(/\//g, '_');
}
